import { useContext, useRef, useState } from 'react'
import { Card, Image } from 'react-bootstrap'
import { ProfileContext } from 'pages/Profile'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { hasMemberNumber, membershipBadgeClass } from 'utils/acl'
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar'

export default function Header() {
  const { profile, setProfile, getProfile } = useContext(ProfileContext)
  const { user: authUser } = useSelector(x => x.auth)
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarUploadError, setAvatarUploadError] = useState('')
  const url = process.env.REACT_APP_URL

  const fullName = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || 'Unnamed User'
  const membershipType = String(profile?.acl || 'Guest')
  const membershipClass = membershipBadgeClass(membershipType)
  const membershipNumber = hasMemberNumber(membershipType)
    ? String(profile?.memberNo || '').trim()
    : ''
  const officerRoles = membershipType === 'Officer'
    ? (Array.isArray(profile?.clubRoles) ? profile.clubRoles : [])
    : []
  const avatarSrc = resolveAvatarSrc(profile?.avatarUrl || profile?.avatar)
  const initials = getUserInitials(profile, 'U')
  const canEditAvatar = Boolean(
    authUser?.user && (authUser.user.acl === 'Officer' || authUser.user._id === profile?._id)
  )

  const handleAvatarClick = (event) => {
    event?.stopPropagation?.()
    if (!canEditAvatar || avatarUploading) return
    fileInputRef.current?.click()
  }

  const handleAvatarKeyDown = (event) => {
    event.stopPropagation()
    if (!canEditAvatar || avatarUploading) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      fileInputRef.current?.click()
    }
  }

  const handleHeaderCardClick = () => {
    const targetUserId = String(profile?._id || '').trim()
    if (!targetUserId) return
    navigate(`/profile/${targetUserId}`, {
      state: {
        profileTab: 'overview',
        source: 'header-card',
        at: Date.now(),
      },
    })
  }

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const allowedMimeTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    if (!allowedMimeTypes.includes(file.type)) {
      alert('Only PNG, JPG/JPEG, or WEBP files are allowed.')
      event.target.value = ''
      return
    }

    try {
      setAvatarUploadError('')
      setAvatarUploading(true)
      const response = await fetch(`${url}/users/${profile?._id}/avatar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authUser?.token}`,
          'Content-Type': file.type,
          'X-Avatar-File-Name': encodeURIComponent(file.name || 'avatar-upload')
        },
        body: file,
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Avatar upload failed (${response.status})`)
      }

      const payload = await response.json().catch(() => null)
      if (payload && typeof payload === 'object' && typeof setProfile === 'function') {
        setProfile(payload)
      }

      if (typeof getProfile === 'function') {
        await getProfile().catch(() => undefined)
      }
    } catch (error) {
      console.error('Error uploading avatar:', error)
      setAvatarUploadError(error?.message || 'Avatar upload failed.')
    } finally {
      setAvatarUploading(false)
    }

    event.target.value = ''
  }

  return (
    <Card
      className="mt-4 mb-4 user-card meetings-mock-card-shell meetings-page-card profile-header-card profile-header-card--clickable"
      onClick={handleHeaderCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleHeaderCardClick()
        }
      }}
    >
      <Card.Body className="meetings-mock-card-body">
        <div className="meetings-mock-card">
          <div className="meetings-mock-top">
            <div className="meetings-mock-date-group">
              <span className={`users-membership-type users-membership-type--${membershipClass}`}>
                {membershipType}
              </span>
              {membershipNumber && (
                <span className="meetings-mock-number">#{membershipNumber}</span>
              )}
            </div>
          </div>

          <div
            className={`meetings-mock-avatar ${canEditAvatar ? 'profile-header-avatar-editable' : ''}`}
            onClick={handleAvatarClick}
            onKeyDown={handleAvatarKeyDown}
            role={canEditAvatar ? 'button' : undefined}
            tabIndex={canEditAvatar ? 0 : undefined}
            title={canEditAvatar ? 'Add or replace avatar' : undefined}
            aria-label={avatarUploading ? `Uploading avatar for ${fullName}` : `Add or replace avatar for ${fullName}`}
            aria-disabled={avatarUploading ? 'true' : undefined}
          >
            {avatarSrc
              ? <Image src={avatarSrc} alt="User avatar" />
              : <span className="meetings-mock-avatar-fallback">{initials}</span>}
            {avatarUploading ? (
              <span className="profile-avatar-processing" aria-hidden="true">
                <span className="profile-avatar-processing-ring" />
                <span className="profile-avatar-processing-label">Processing</span>
              </span>
            ) : null}
            {canEditAvatar && (
              <input
                className="profile-header-avatar-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                ref={fileInputRef}
                onClick={(event) => event.stopPropagation()}
                onChange={handleFileChange}
              />
            )}
          </div>

          {avatarUploading ? (
            <p className="profile-avatar-status">Processing avatar. Background removal and upload may take a moment.</p>
          ) : null}
          {avatarUploadError ? <p className="profile-avatar-status text-danger">{avatarUploadError}</p> : null}

          <h3 className="meetings-mock-title">{fullName}</h3>
          <div className="meetings-mock-separator"></div>
          {membershipType === 'Officer' ? (
            <>
              <div className="meetings-mock-role-label">OFFICER ROLE:</div>
              <div className="meetings-mock-role-name">
                {officerRoles.length > 0
                  ? officerRoles.map((role, roleIndex) => (
                    <div key={`profile-role-${roleIndex}`}>{role}</div>
                  ))
                  : <div>Not assigned in controller</div>}
              </div>
            </>
          ) : (
            <div className="user-role-placeholder" aria-hidden="true"></div>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}